import { forwardRef } from 'preact/compat';
import { useCallback, useRef } from 'preact/hooks';
import type { JSX } from 'preact';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { SwitchField } from './SwitchField';
import { StyledSwitch } from '#PRIVATE_StyledSwitch/StyledSwitch';
import type { ComponentProps } from 'preact';
import type { Size } from '#utils/UNSAFE_size';
import type { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { UnpackSignals } from '#utils/UNSAFE_attributeUtils';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useSwitch } from './useSwitch';
import { Label } from '#UNSAFE_Label';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { SwitchRedwoodTheme } from './themes/redwood/SwitchTheme';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

export type Props = TestIdProps & {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the switch.
   */
  'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];
  /**
   * Text to provide guidance to help the user understand what to toggle the switch to.
   */
  assistiveText?: string;
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: string;
  /**
   * Custom text for the help source link.
   */
  helpSourceText?: string;
  /**
   * Whether the switch is disabled and cannot be toggled.
   */
  isDisabled?: boolean;
  /**
   * Whether the switch is readonly and user cannot change its state.
   */
  isReadonly?: boolean;
  /**
   * Specifies the label associated with the field.
   */
  label: string;
  /**
   * Where the label is positioned relative to the field. Possible values are 'inside', 'start', 'top', or 'none'.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';
  /**
   * The width of the label when labelEdge is 'start'.
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * Callback invoked when the user toggles the switch.
   */
  onCommit?: (detail: ValueUpdateDetail<boolean>) => void;
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'compact'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Whether the switch is currently on (true) or off (false).
   */
  value?: boolean;
};

/**
 * The Switch component toggles between two mutually exclusive states, such as on and off.
 *
 */
export const Switch = forwardRef<HTMLDivElement, Props>(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      onCommit,
      helpSourceLink,
      helpSourceText,
      testId,
      userAssistanceDensity: propUserAssistanceDensity,
      value: propValue
    }: Props,
    ref
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const { baseId, formFieldContext, styledSwitchProps, labelProps, userAssistanceProps } =
      useSwitch({
        ariaDescribedBy,
        helpSourceLink,
        helpSourceText,
        isDisabled,
        isReadonly,
        label,
        labelEdge,
        messages,
        userAssistanceDensity,
        value: propValue
      });

    // This ref is used for both imperative focus and enabling SwitchField to "click label to focus"
    const switchElementRef = useRef<HTMLDivElement>(null);

    // setRefs is passed as the ref prop to <StyledSwitch> below.
    // It synchronizes both:
    // - switchElementRef (internal, for label click/focus logic)
    // - the parent/test ref (for imperative .focus()/.blur() calls)
    // so that both internal and external focus logic work with the same DOM node.
    function setRefs(node: HTMLDivElement | null) {
      switchElementRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }

    // Label click focus logic
    const onClick = useCallback(() => {
      if (switchElementRef) {
        switchElementRef.current?.focus();
      }
    }, [switchElementRef]);

    const labelComp =
      labelEdge !== 'none' ? (
        <Label
          {...labelProps}
          {...(!isDisabled && !isReadonly ? { onClick } : {})}
          parentComponentVariant="switch">
          {label}
        </Label>
      ) : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    // According to UX, Switch's compact user assistance density behaves like efficient.
    const resolvedUserAssistanceDensity =
      userAssistanceDensity === 'compact' ? 'efficient' : userAssistanceDensity;
    const isInlineDensity =
      resolvedUserAssistanceDensity === 'efficient' || resolvedUserAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled fields.
        // showInlineAssistanceOn is 'always' because for Switch we always show the user assistance.
        resolvedUserAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            showInlineAssistanceOn="always"
            userAssistanceDensity={resolvedUserAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : isReadonly ? (
        <InlineUserAssistance
          fieldLabel={label}
          showInlineAssistanceOn="always"
          messages={messages}
          userAssistanceDensity={resolvedUserAssistanceDensity}
          {...userAssistanceProps}
        />
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          showInlineAssistanceOn="always"
          messages={messages}
          userAssistanceDensity={resolvedUserAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    // toggle the value when we get the StyledSwitch's onChange handler is activated
    const onChangeHandler = useCallback(() => {
      const previousValue = !!propValue;
      const value = !previousValue;

      onCommit?.({
        previousValue,
        value
      });
    }, [onCommit, propValue]);

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const switchOn = translations.switch_on();
    const switchOff = translations.switch_off();

    const testIdProps = useTestId(testId);
    const styledSwitchTestId = testId && `${testId}-styled-switch`;

    const styledSwitchComp = (
      <StyledSwitch
        {...styledSwitchProps}
        baseId={baseId}
        isChecked={propValue}
        isDisabled={isDisabled}
        isReadonly={isReadonly}
        readonlyOnText={switchOn}
        readonlyOffText={switchOff}
        ref={setRefs}
        testId={styledSwitchTestId}
        onChange={onChangeHandler}
      />
    );

    return (
      <div {...testIdProps} className={SwitchRedwoodTheme.baseTheme}>
        <FormFieldContext.Provider value={formFieldContext}>
          <SwitchField
            columnSpan={columnSpan}
            inlineUserAssistance={inlineUserAssistance}
            mainContent={styledSwitchComp}
            {...fieldLabelProps}
          />
        </FormFieldContext.Provider>
      </div>
    );
  }
);
